import {
  createEmptyDocument,
  openDocument,
  sameContent,
  type DocumentId,
  type DocumentIssue,
  type EditorDocument,
  type OpenOptions,
  type StorageRevision,
  type StoredDocument,
} from "../index";
import type { CommitInput, DocumentRepository } from "./repository";

export type SessionPhase = "idle" | "loading" | "ready" | "unreadable" | "disposed";

/**
 * Local durability only. `saved` means every edit up to `savedSequence` finished a durable local
 * transaction; it says nothing about remote sync, which the host reports through `setSyncStatus`.
 */
export type SaveStatus = "clean" | "dirty" | "saving" | "saved" | "failed" | "conflict";

export type SyncStatus = "unknown" | "syncing" | "synced" | "offline" | "error";

export type FlushResult = "saved" | "failed" | "conflict";

export interface SessionError {
  code: "quota" | "storage" | "load";
  message: string;
}

export interface SessionConflict {
  /** What this session tried to write. */
  local: EditorDocument;
  /** What another writer stored first. */
  remote: StoredDocument;
}

export interface SessionState {
  phase: SessionPhase;
  save: SaveStatus;
  sync: SyncStatus;
  /** Count of content changes in this session. */
  editSequence: number;
  /** Highest edit sequence acknowledged by a durable local commit. */
  savedSequence: number;
  storageRevision: StorageRevision | null;
  /**
   * Bumps whenever the editor must be rebuilt from `getDocument()`: the first load, and a stored
   * copy that an attached editor could not take in place. Never for a revision whose content is
   * what the editor already shows.
   */
  generation: number;
  error: SessionError | null;
  conflict: SessionConflict | null;
  /** A revision written elsewhere while this session was clean; the host decides when to reload. */
  externalRevision: StorageRevision | null;
  unreadable: { raw: string; issue: DocumentIssue } | null;
}

export interface DocumentSessionOptions extends OpenOptions {
  documentId: DocumentId;
  repository: DocumentRepository;
  /** Quiet period after the last edit before committing. */
  debounceMs?: number;
  /** Longest a continuously edited document goes without a commit. */
  maxWaitMs?: number;
  /** Delays between retries of a rejected commit; after the last one the session reports `failed`. */
  retryDelaysMs?: readonly number[];
  createMutationId?: () => string;
}

/**
 * What survives when a stored copy replaces what the editor shows. Replacing is never an edit: it
 * adds no undo step, marks nothing unsaved, and writes nothing back.
 */
export interface RevisionPolicy {
  /**
   * `keep` (the default) puts each end of the selection back in the block it was in, by block ID,
   * at the same character offset or the block's end if it is now shorter; if the block is gone, at
   * the start of the block now standing in its place. `reset` moves it to the start of the
   * document. With `keep`, an editor that had no selection gets none.
   */
  selection?: "keep" | "reset";
  /**
   * `clear` (the default) empties the undo stack, so Undo cannot reach back past the other
   * writer's change. With `keep` it can: the state Undo returns to predates that change, and
   * returning to it is an edit of the user's, which saves over it.
   */
  history?: "clear" | "keep";
}

/** What an attached editor offers the session. Only `snapshot` is required. */
export interface LiveDocument {
  snapshot(): EditorDocument;
  /**
   * Resolves once no IME composition is open, at once if none is. Replacing the content in the
   * middle of a composition destroys what is being typed, so the session waits for this first.
   */
  settled?(): Promise<void>;
  /**
   * Replaces the live content in place. Answers `false` if it could not, and the session then has
   * the editor rebuilt instead.
   */
  replace?(document: EditorDocument, policy: Required<RevisionPolicy>): boolean;
}

export interface DocumentSession {
  readonly documentId: DocumentId;
  getState(): SessionState;
  subscribe(listener: () => void): () => void;
  /** Reads the stored document. Safe to call again; concurrent calls share one load. */
  load(): Promise<SessionState>;
  /** The document the editor should build from. Throws unless the phase is `ready`. */
  getDocument(): EditorDocument;
  /**
   * Registers the live editor: a snapshot source, or a `LiveDocument` that can also be replaced in
   * place. Snapshots are only taken when committing. Detaching while dirty captures one final
   * snapshot, so a save can still finish after the editor unmounts.
   */
  attach(source: (() => EditorDocument) | LiveDocument): () => void;
  /** Records a content change (never a selection-only change) and schedules a commit. */
  notifyChange(): number;
  /** Commits everything pending and resolves once it is durable, failed, or in conflict. */
  flush(): Promise<FlushResult>;
  /**
   * Applies what is stored, which is how a host takes an `externalRevision`. It refuses (`false`)
   * while there are unsaved edits, including ones made while it was reading or waiting for a
   * composition to end. A revision with unchanged content is taken without touching the editor;
   * changed content replaces the editor's in place, under `policy`.
   */
  reload(policy?: RevisionPolicy): Promise<boolean>;
  /** `take-remote` replaces the editor's content the way `reload` does, under `policy`. */
  resolveConflict(
    choice: "keep-local" | "take-remote",
    policy?: RevisionPolicy,
  ): Promise<FlushResult>;
  setSyncStatus(sync: SyncStatus): void;
  /** Stops scheduling and listening. An in-flight commit still completes. */
  dispose(): Promise<void>;
}

interface PendingMutation {
  input: CommitInput;
  sequence: number;
}

const isQuotaError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  (error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED");

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export function createDocumentSession(options: DocumentSessionOptions): DocumentSession {
  const {
    documentId,
    repository,
    debounceMs = 200,
    maxWaitMs = 1000,
    retryDelaysMs = [250, 1000, 4000],
    createMutationId = () => crypto.randomUUID(),
  } = options;

  let state: SessionState = {
    phase: "idle",
    save: "clean",
    sync: "unknown",
    editSequence: 0,
    savedSequence: 0,
    storageRevision: null,
    generation: 0,
    error: null,
    conflict: null,
    externalRevision: null,
    unreadable: null,
  };
  const listeners = new Set<() => void>();
  let document: EditorDocument | null = null;
  let live: LiveDocument | null = null;
  let loading: Promise<SessionState> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let firstDirtyAt: number | null = null;
  let writer: Promise<void> | null = null;
  // A rejected commit may still have been stored. It is retried under the same mutation ID before
  // any newer content is sent, otherwise the next write would conflict with our own.
  let pending: PendingMutation | null = null;
  // The edit sequence of the commit that makes a migration durable. No user edit is part of it, so
  // if it loses a race there is nothing to keep and the other writer's copy is simply adopted.
  let migrationSequence: number | null = null;
  let unsubscribeRepository: (() => void) | null = null;
  const ownRevisions = new Set<StorageRevision>();

  const set = (patch: Partial<SessionState>): void => {
    state = { ...state, ...patch };
    for (const listener of listeners) listener();
  };

  const canWrite = (): boolean =>
    state.phase === "ready" && state.conflict === null && state.unreadable === null;

  const clearTimer = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const schedule = (): void => {
    if (!canWrite()) return;
    const now = Date.now();
    firstDirtyAt ??= now;
    clearTimer();
    const delay = Math.max(0, Math.min(debounceMs, firstDirtyAt + maxWaitMs - now));
    timer = setTimeout(() => {
      timer = null;
      void drain();
    }, delay);
  };

  const snapshot = (): EditorDocument => {
    if (live !== null) document = live.snapshot();
    return document!;
  };

  const isDirty = (): boolean => state.savedSequence < state.editSequence;
  const isDisposed = (): boolean => state.phase === "disposed";
  const isReady = (): boolean => state.phase === "ready";

  // Serial writer: one commit in flight per session, and every call joins the same run.
  const drain = (): Promise<void> => {
    writer ??= write().finally(() => {
      writer = null;
    });
    return writer;
  };

  async function write(): Promise<void> {
    let attempt = 0;
    while (canWrite() && (pending !== null || state.savedSequence < state.editSequence)) {
      firstDirtyAt = null;
      pending ??= {
        sequence: state.editSequence,
        input: {
          document: snapshot(),
          expectedStorageRevision: state.storageRevision,
          mutationId: createMutationId(),
        },
      };
      const current = pending;
      set({ save: "saving", error: null });
      try {
        // oxlint-disable-next-line no-await-in-loop -- a serial writer: one commit at a time
        const result = await repository.commit(current.input);
        pending = null;
        attempt = 0;
        if (result.status === "conflict") {
          let local = current.input.document;
          if (current.sequence === migrationSequence && state.editSequence === migrationSequence) {
            // oxlint-disable-next-line no-await-in-loop -- never replace content mid-composition
            await live?.settled?.();
            if (isDisposed()) return;
            if (state.editSequence === migrationSequence) {
              adopt(result.current);
              continue;
            }
            // What was composed meanwhile is the user's: from here it is an ordinary conflict.
            local = snapshot();
          }
          set({ save: "conflict", conflict: { local, remote: result.current } });
          return;
        }
        ownRevisions.add(result.storageRevision);
        const savedSequence = Math.max(state.savedSequence, current.sequence);
        set({
          storageRevision: result.storageRevision,
          savedSequence,
          // An acknowledgement for older content never marks newer edits as saved.
          save: savedSequence === state.editSequence ? "saved" : "dirty",
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        const delay = retryDelaysMs[attempt];
        if (isQuotaError(error) || delay === undefined) {
          set({
            save: "failed",
            error: { code: isQuotaError(error) ? "quota" : "storage", message },
          });
          return;
        }
        attempt++;
        // oxlint-disable-next-line no-await-in-loop -- backoff before retrying the same mutation
        await wait(delay);
      }
    }
  }

  const settle = (): FlushResult => {
    if (state.conflict !== null) return "conflict";
    if (state.save === "failed" || state.savedSequence < state.editSequence) return "failed";
    return "saved";
  };

  const adopt = (stored: StoredDocument | null, policy: RevisionPolicy = {}): void => {
    const opened =
      stored === null
        ? ({ status: "ready", document: createEmptyDocument(documentId), migrated: false } as const)
        : openDocument(stored.document, options);
    if (opened.status === "unreadable") {
      document = null;
      set({ phase: "unreadable", unreadable: { raw: opened.raw, issue: opened.issue } });
      return;
    }
    // Only an editor that is showing a document can keep showing one. The first load, and a
    // session coming back from `unreadable`, always build it.
    const showing = state.phase === "ready" && document !== null;
    // Version metadata alone is no reason to touch the editor.
    const unchanged = showing && !opened.migrated && sameContent(snapshot(), opened.document);
    const inPlace =
      unchanged ||
      (showing &&
        live?.replace?.(opened.document, {
          selection: policy.selection ?? "keep",
          history: policy.history ?? "clear",
        }) === true);
    if (!unchanged) document = opened.document;
    // An editor that refused this content is showing something else, and is about to be rebuilt.
    // It no longer speaks for the document: a snapshot taken from it now (by the commit below, or
    // by its own detach, which saves unsaved typing) would put what it showed over what was just
    // adopted, and store that. An editor without tables that is handed its first table is the case.
    if (showing && !inPlace) live = null;
    pending = null;
    set({
      phase: "ready",
      save: "clean",
      storageRevision: stored?.storageRevision ?? null,
      savedSequence: state.editSequence,
      generation: inPlace ? state.generation : state.generation + 1,
      error: null,
      conflict: null,
      externalRevision: null,
      unreadable: null,
    });
    migrationSequence = null;
    if (opened.migrated) {
      // What is in memory is newer than what is stored (a schema upgrade, or blocks that were given
      // IDs). Commit it once, so stored IDs stop depending on every reader deriving the same ones.
      migrationSequence = state.editSequence + 1;
      set({ editSequence: migrationSequence, save: "dirty" });
      schedule();
    }
  };

  async function read(): Promise<SessionState> {
    set({ phase: "loading", error: null });
    try {
      const result = await repository.load(documentId);
      if (state.phase === "disposed") return state;
      if (result.status === "unreadable") {
        set({ phase: "unreadable", unreadable: { raw: result.raw, issue: result.reason } });
      } else {
        adopt(result.status === "ok" ? result.stored : null);
      }
    } catch (error) {
      if (state.phase === "disposed") return state;
      const message = error instanceof Error ? error.message : String(error);
      // A failed read is not an empty document: stay out of `ready` so nothing can overwrite it.
      set({ phase: "idle", error: { code: "load", message } });
    }
    return state;
  }

  const session: DocumentSession = {
    documentId,
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    load() {
      if (state.phase === "disposed") return Promise.resolve(state);
      if (state.phase === "ready" || state.phase === "unreadable") return Promise.resolve(state);
      loading ??= read().finally(() => {
        loading = null;
      });
      unsubscribeRepository ??=
        repository.subscribe?.(documentId, (revision) => {
          if (ownRevisions.delete(revision) || revision === state.storageRevision) return;
          // Dirty sessions learn about the other writer from their next commit's conflict result.
          if (state.save === "clean" || state.save === "saved") set({ externalRevision: revision });
        }) ?? null;
      return loading;
    },
    getDocument() {
      if (state.phase !== "ready" || document === null) {
        throw new Error(`Session for "${documentId}" has no readable document (${state.phase}).`);
      }
      return document;
    },
    attach(next) {
      const attached: LiveDocument = typeof next === "function" ? { snapshot: next } : next;
      live = attached;
      return () => {
        if (live !== attached) return;
        if (canWrite() && isDirty()) document = attached.snapshot();
        live = null;
      };
    },
    notifyChange() {
      if (state.phase !== "ready") return state.editSequence;
      const editSequence = state.editSequence + 1;
      set({ editSequence, save: state.save === "saving" ? "saving" : "dirty" });
      schedule();
      return editSequence;
    },
    async flush() {
      clearTimer();
      if (state.save === "failed") set({ save: "dirty" });
      // Loop: edits made while a commit was in flight need a commit of their own.
      // oxlint-disable-next-line no-await-in-loop -- each pass waits for the writer to go idle
      do await drain();
      while (canWrite() && state.save !== "failed" && state.savedSequence < state.editSequence);
      return settle();
    },
    async reload(policy) {
      if (isDisposed() || isDirty()) return false;
      await writer;
      if (!isReady()) {
        // Nothing is being shown (a failed or unreadable load): this is a load, not a replacement.
        await read();
        return isReady();
      }
      const flagged = state.externalRevision;
      let result: Awaited<ReturnType<DocumentRepository["load"]>>;
      try {
        // The phase stays `ready` throughout: a working editor is not torn down to read storage.
        result = await repository.load(documentId);
      } catch {
        return false;
      }
      // The user may have typed, or started composing, while storage was being read.
      await live?.settled?.();
      if (isDisposed() || isDirty()) return false;
      if (result.status === "unreadable") {
        document = null;
        set({ phase: "unreadable", unreadable: { raw: result.raw, issue: result.reason } });
        return false;
      }
      const stored = result.status === "ok" ? result.stored : null;
      const latest = state.externalRevision;
      adopt(stored, policy);
      // A revision announced during the read that is newer than what was read is still news.
      if (latest !== null && latest !== flagged && latest !== state.storageRevision) {
        set({ externalRevision: latest });
      }
      return isReady();
    },
    async resolveConflict(choice, policy) {
      if (state.conflict === null) return settle();
      if (choice === "take-remote") {
        await live?.settled?.();
        const conflict = state.conflict;
        if (isDisposed() || conflict === null) return settle();
        adopt(conflict.remote, policy);
        return settle();
      }
      const conflict = state.conflict;
      // Keep local: rebase onto the other writer's revision and write again. The remote version
      // stays available to the host through the state it already observed.
      set({
        conflict: null,
        save: "dirty",
        storageRevision: conflict.remote.storageRevision,
        editSequence: state.editSequence + 1,
      });
      return session.flush();
    },
    setSyncStatus(sync) {
      if (sync !== state.sync) set({ sync });
    },
    async dispose() {
      if (state.phase === "disposed") return;
      clearTimer();
      unsubscribeRepository?.();
      unsubscribeRepository = null;
      const inFlight = writer;
      set({ phase: "disposed" });
      listeners.clear();
      live = null;
      await inFlight;
    },
  };
  return session;
}
